import { AssigneeSelect } from "@/components/AssigneeSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from 'react-router-dom';
import { PrioritySelect } from "./PrioritySelect";
import { StatusSelect } from "@/components/StatusSelect";
import { ReleaseSelect } from "@/components/ReleaseSelect";
import { cn } from "@/lib/utils";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from "date-fns";
import { TaskContextMenu } from './TaskContextMenu';

export function TaskListItem({
  task,
  organizationSlug,
  className,
  onTaskUpdate,
  onSelectionChange,
  isSelected,
  isDragging,
  isDragDisabled = false,
  columnConfig = {
    checkbox: true,
    key: true,
    summary: true,
    status: true,
    assignee: true,
    priority: false,
    release: true,
    dueDate: false,
    actions: false
  },
  ...props
}) {
  const navigate = useNavigate();

  const handleTaskChange = (updatedTask) => {
    const mappedTask = {
      ...task,
      ...updatedTask,
      id: updatedTask._id || task.id,
      status: updatedTask.status || task.status,
      assignee: updatedTask.assignee,
      priority: updatedTask.priority || task.priority,
      order: task.order
    };

    if (onTaskUpdate) {
      onTaskUpdate(mappedTask);
    }
  };

  const handleRowClick = (e) => {
    if (e.target.closest('.task-action')) {
      e.stopPropagation();
      return;
    }
    
    // If in bulk selection mode, clicking toggles selection
    if (isDragDisabled) {
      handleCheckboxChange(!isSelected);
      return;
    }
    
    navigate(`/${organizationSlug}/plan/${task.key}`);
  };

  const handleCheckboxChange = (checked) => {
    if (onSelectionChange) {
      onSelectionChange(task.id, checked);
    }
  };

  // Build grid columns dynamically based on config
  const getGridTemplateColumns = () => {
    const columns = [];
    if (columnConfig.checkbox) columns.push('auto');
    if (columnConfig.key) columns.push('80px');
    if (columnConfig.summary) columns.push('1fr');
    if (columnConfig.status) columns.push('140px');
    if (columnConfig.assignee) columns.push('160px');
    if (columnConfig.priority) columns.push('100px');
    if (columnConfig.release) columns.push('160px');
    if (columnConfig.dueDate) columns.push('120px');
    return columns.join(' ');
  };

  return (
    <TaskContextMenu task={task} organizationSlug={organizationSlug}>
      <div
        onClick={handleRowClick}
        className={cn(
          "grid gap-4 items-center p-3 rounded-lg dark:bg-neutral-950 dark:hover:bg-neutral-900 bg-neutral-100 hover:bg-neutral-200 h-16 transition-all",
          isSelected && "dark:bg-neutral-900 bg-neutral-200 ring-2 ring-violet-500 ring-offset-0 ring-offset-background",
          isDragging && "rotate-1 scale-105 ring-2 ring-violet-500 ring-offset-2 ring-offset-background shadow-lg",
          isDragDisabled ? "cursor-default select-none group" : "cursor-pointer",
          className
        )}
        style={{ gridTemplateColumns: getGridTemplateColumns() }}
        {...props}
      >
      {columnConfig.checkbox && (
        <div className="task-action flex items-center justify-center w-8 h-8 cursor-default" onClick={(e) => {
          e.stopPropagation();
          handleCheckboxChange(!isSelected);
        }}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleCheckboxChange}
            className={cn(
              "cursor-default group-hover:ring-2 group-hover:ring-primary data-[state=checked]:ring-2 data-[state=checked]:ring-primary",
              isDragDisabled && "group-hover:ring-2 group-hover:ring-primary"
            )}
          />
        </div>
      )}

      {columnConfig.key && (
        <div className="text-sm font-medium text-muted-foreground">{task.key}</div>
      )}

      {columnConfig.summary && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-sm truncate min-w-[100px]">
                {task.summary}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{task.summary}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {columnConfig.status && (
        <div className="task-action">
          <StatusSelect
            task={task}
            onTaskChange={handleTaskChange}
          />
        </div>
      )}

      {columnConfig.assignee && (
        <div className="task-action">
          <AssigneeSelect
            task={task}
            organizationSlug={organizationSlug}
            onTaskChange={handleTaskChange}
            showName
          />
        </div>
      )}

      {columnConfig.priority && (
        <div className="flex task-action">
          <PrioritySelect
            task={task}
            onTaskChange={handleTaskChange}
          />
        </div>
      )}

      {columnConfig.release && (
        <div className="task-action">
          <ReleaseSelect
            task={task}
            onTaskChange={handleTaskChange}
          />
        </div>
      )}

      {columnConfig.dueDate && (
        <div>
          <span className="text-muted-foreground">
            {task.dueDate ? format(new Date(task.dueDate), "MMM d, yyyy") : 'No Due Date'}
          </span>
        </div>
      )}
      </div>
    </TaskContextMenu>
  );
}
